import { DatePipe, NgIf, NgClass } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Select2, Select2Data } from 'ng-select2-component';
import { Subject, Subscription, debounceTime, filter, tap, switchMap } from 'rxjs';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { APP_CONSTANTS } from '../../app.constants';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { UserInfo } from '../../models/users.model';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-company-reports',
  imports: [ReactiveFormsModule, PaginationComponent, BreadcrumbComponent, NgSelectModule,Select2, NgIf, NgClass, DatePipe],
  templateUrl: './company-reports.component.html',
  styleUrl: './company-reports.component.css'
})
export class CompanyReportsComponent {
  breadcrumbList = {
      breadcrumb_path: 'Dashboard',
      currentURL: 'Company Reports',
      breadcrumb_url: '/admin'
    }
    active = 1;
    page: any = 1;
    totalRows: number = 20;
    totalPage: any = 0;
    allData: any = [];
    offset_limit: number = 20;
    hasMore: boolean = true;
    lastSearchTerm: any;
    propertiesList: any = [];
    disableClear: boolean = false;
    searchForm!: FormGroup;
    searchInput$ = new Subject<string>();
    properties: Select2Data = [];
    allUsers: any = [];
    orderData: any = [];
    allOwners: any = [];
    private userInfo : UserInfo;
    cities_list: Select2Data = [];
    private httpService = inject(HttpServiceService);
    private authService = inject(LocalStorageService);
    loadingService = inject(LoadingService);
    private lastSearchTermSubscription?: Subscription;
    private searchFormSubscription?: Subscription;
    private getAllOwnerReportsSubscription?: Subscription;
    private getAllOwnersSubscription?: Subscription;
    pageCount : number = 1;
    private destroyRef = inject(DestroyRef);
  
    constructor(private fb: FormBuilder, private alertService: NgxToastrService, private route: ActivatedRoute) {
      this.searchForm = this.fb.group({
        property_id: [null],
        owner: [null],
        city: [null],
        type: [null],
        from_date : [''],
        to_date: [{ value: '', disabled: true }],
      });
      this.userInfo = this.authService.getItem("userInfo") as UserInfo;    
      this.getAllCompanyReports();
    }
  
    ngOnInit(): void {
      let cities = this.route.snapshot.data['data']?.["data"] ?? [];
      for (let city of cities) {
        this.cities_list.push(
          {
            value: city,
            label: city,
            data: { name: city }
          }
        )
      }
      this.getAllProperties();
      this.lastSearchTermSubscription = this.searchInput$
        .pipe(
          debounceTime(300),
          filter(term => {
            const skip = term === this.lastSearchTerm;
            return !skip;
          }),
          tap((term) => {
            this.page = 1;
            this.hasMore = true;
            this.lastSearchTerm = term;
          }),
          switchMap(term => {
            let query = term ? "&name=" + term : '';
            return this.httpService.getAllProperties(1, 20, query);
          }
          )
        )
        .subscribe(results => {
          this.properties = [];
          this.propertiesList = results.data["properties"] ?? [];
          if (this.propertiesList.length >= results.data["total"]) {
            this.hasMore = false;
          }
          for (let resp of this.propertiesList) {
            this.properties.push(
              {
                value: resp.uid,
                label: resp.name,
                data: { name: resp.name }
              }
            )
          }
        });
        this.searchForm.get('from_date')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        const toDateControl = this.searchForm.get('to_date');
        if (value) {
          toDateControl!.enable();
        } else {
          toDateControl!.disable();
          toDateControl!.reset(); // Optionally clear value
        }
      });
      
    }
  
    getAllProperties(query: any = '') {
      this.httpService.getAllProperties(this.page, this.offset_limit).subscribe({
        next: (response) => {
          this.propertiesList = response.data["properties"] ?? [];
          if (this.propertiesList.length >= response.data["total"]) {
            this.hasMore = false;
          }
          this.properties = [];
          for (let resp of this.propertiesList) {
            this.properties.push(
              {
                value: resp.uid,
                label: resp.name,
                data: { name: resp.name }
              }
            )
          }
        }
      })
    }
  
    onScrollToEnd() {
      // Optional: For pagination / infinite scroll
      if (!this.hasMore) return;
      this.page += 1;
      this.offset_limit += 20;
      this.httpService.getAllProperties(this.page, this.offset_limit).subscribe(response => {
        let resp = response.data["properties"] ?? [];
        if (this.propertiesList.length >= response.data["total"]) {
          this.hasMore = false;
        }
        this.propertiesList = [...this.propertiesList, ...resp];
        this.properties = [];
        for (let resp of this.propertiesList) {
          this.properties.push(
            {
              value: resp.uid,
              label: resp.name,
              data: { name: resp.name }
            }
          )
        }
      });
    }
  
    handleSearch(value: any, keepPage: string = '') {
      const searchData = this.searchForm.value;
      let query = '';
      for (let key in searchData) {
        if (searchData[key]) {
          let search_val = searchData[key];
          if (key == 'property_id') {
            search_val = search_val.value ?? "";
          }
          query += `&${key}=${search_val}`;
        }
      }
      if (!keepPage) this.page = 1;
      if (query || !value) this.getAllCompanyReports(query);
      this.disableClear = (value || query) ? true : false;
    }
  
    resetSearchForm() {
      this.page = 1;
      this.searchForm.reset({
        inspection_status: null
      }, { emitEvent: false });
      this.getAllCompanyReports();
      this.disableClear = false;
    }
  
    getAllCompanyReports(query: any = '') {
      this.getAllOwnerReportsSubscription = this.httpService.getAllServiceRequests(this.page, this.offset_limit, query).subscribe({
        next: (response) => {
          this.allUsers = response.data['serviceRequests'] ?? [];
          this.orderData = this.allUsers.slice();
          this.allData = this.paginator(this.orderData, this.page, this.totalRows, response["data"]?.total);
          this.totalPage = response.data.total;
         this.pageCount = response.data.pageCount;
        }
      });
    }
  
    getAllOwners() {
      let ownerQuery = `&role_identifier=${APP_CONSTANTS.USER_ROLES.PROPERTY_MANAGER}`;
      this.getAllOwnersSubscription = this.httpService.getAllUsers(this.page, this.offset_limit, ownerQuery).subscribe({
        next: (response) => {
          this.allOwners = response.data.users?.map((user: { first_name: string; last_name: string; }) => ({
            ...user,
            fullName: `${user.first_name} ${user.last_name}`
          }));
        },
        error: (error) => {
          console.log(error);
        }
      })
    }
  
    pageChange(e: any) {    //  Page Change funcation   ---------
      if (this.page == e) return;
      this.page = e;
    }
  
    paginator(items: any, current_page: any, per_page_items: any, total_records: any) {
      let page = current_page || 1,
        per_page = per_page_items || 10,
        offset = (page - 1) * per_page,
  
        paginatedItems = items,
        total_pages = Math.ceil(items.length / per_page);
  
  
      return {
        page: page,
        startPage: offset + 1,
        endPage: page * per_page,
        per_page: per_page,
        pre_page: page - 1 ? page - 1 : null,
        next_page: (total_pages > page) ? page + 1 : null,
        total: total_records,
        total_pages: total_pages,
        data: paginatedItems
      };
    }
  
    get isPropertyManager(){
        if(this.userInfo?.["role_identifier"] == APP_CONSTANTS.USER_ROLES.PROPERTY_MANAGER){
          return true;
        }
        return false;
    }
  
    ngOnDestroy() {
      if (this.lastSearchTermSubscription) {
        this.lastSearchTermSubscription.unsubscribe();
      }
      if (this.searchFormSubscription) {
        this.searchFormSubscription.unsubscribe();
      }
      if (this.getAllOwnerReportsSubscription) {
        this.getAllOwnerReportsSubscription.unsubscribe();
      }
      if (this.getAllOwnersSubscription) {
        this.getAllOwnersSubscription.unsubscribe();
      }
    }
  
}
